import React, { createContext, useState, useContext, useEffect, useRef } from 'react';
import axios from 'axios';

const AuthContext = createContext(null);

const API_URL = '/api';

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);
  const heartbeatRef = useRef(null);

  useEffect(() => {
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      fetchCurrentUser();
    } else {
      setLoading(false);
    }
  }, [token]);

  // Heartbeat: set online every 30s
  useEffect(() => {
    if (user && token) {
      const sendHeartbeat = () => { axios.post(`${API_URL}/users/heartbeat`).catch(() => {}); };
      sendHeartbeat();
      heartbeatRef.current = setInterval(sendHeartbeat, 30000);
      return () => clearInterval(heartbeatRef.current);
    }
  }, [user, token]);

  const fetchCurrentUser = async () => {
    try {
      const response = await axios.get(`${API_URL}/auth/me`, { timeout: 10000 });
      setUser(response.data);
    } catch (error) {
      console.error('Errore fetch utente:', error);
      logout();
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    try {
      const response = await axios.post(`${API_URL}/auth/login`, { email, password }, { timeout: 15000 });
      const { access_token, user: userData } = response.data;
      
      localStorage.setItem('token', access_token);
      setToken(access_token);
      setUser(userData);
      axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
      
      return { success: true, user: userData };
    } catch (error) {
      if (error.code === 'ECONNABORTED' || !error.response) {
        return { success: false, error: 'Impossibile raggiungere il server. Controlla la connessione.' };
      }
      return { success: false, error: error.response?.data?.detail || 'Errore login' };
    }
  };

  const logout = async () => {
    try {
      if (token) {
        await axios.post(`${API_URL}/users/offline`).catch(() => {});
        await axios.post(`${API_URL}/auth/logout`);
      }
    } catch (error) {
      console.error('Errore logout:', error);
    } finally {
      clearInterval(heartbeatRef.current);
      localStorage.removeItem('token');
      setToken(null);
      setUser(null);
      delete axios.defaults.headers.common['Authorization'];
    }
  };

  const value = {
    user,
    token,
    loading,
    login,
    logout,
    isAdmin: user?.ruolo === 'super_admin',
    isDipendente: user?.ruolo === 'dipendente'
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth deve essere usato all\'interno di AuthProvider');
  }
  return context;
};